import React, {useState, useEffect, useMemo} from 'react';
import axios from 'axios';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import DetailUser from './DetailUser';
import {usePermissions} from '../../utils/usePermissions';
import {
  validateClientForm as validateClientFormUtil,
  validateDossierForm as validateDossierFormUtil,
  extractBackendError,
  mapBackendErrorToField
} from '../../utils/clientDossierValidator';
import './UsersManagement.css';

const UsersManagement = ({onNavigate, onLogout, currentPage}) => {
  const {hasPermission} = usePermissions();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [clientFieldErrors, setClientFieldErrors] = useState({});

  // États pour le filtrage et le tri
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({key: 'nom', direction: 'ascending'});

  // États pour les modales
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showConvertToClientModal, setShowConvertToClientModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // État pour le formulaire
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    mail: '',
    typeUser: 'user',
    isActive: true,
    permissions: []
  });

  // État pour le formulaire de conversion en client
  const [clientFormData, setClientFormData] = useState({
    typeClient: 'BT',
    typeIdentifiant: 'cin',
    cin: '',
    passeport: '',
    telephone: '',
    email: '',
    adresse: '',
    // Premier dossier
    referenceSteg: '',
    puissance: '',
    district: '',
    typePaiement: 'comptant',
    etatDossier: 'Déposé chez Sastra',
    dateArrivDossier: new Date().toISOString().split('T')[0],
  });
  const [stegList, setStegList] = useState([]);

  const clearMessages = () => {
    setError(null);
    setMessage(null);
  };

  const API_URL = import.meta.env.VITE_API_URL;

  const getApiUrl = (endpoint) => {
    const baseUrl = API_URL.endsWith('/api') ? API_URL : `${API_URL}/api`;
    return `${baseUrl}${endpoint}`;
  };

  useEffect(() => {
    const userType = localStorage.getItem('typeUser');
    if (userType !== 'admin' && userType !== 'moderateur') {
      onNavigate('profile');
      return;
    }
    fetchUsers();
    fetchStegList();
  }, []);

  const fetchStegList = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(getApiUrl('/steg'), {
        headers: {Authorization: `Bearer ${token}`}
      });
      setStegList(res.data?.data || res.data || []);
    } catch (err) {
      console.error('Erreur chargement STEG:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Token non trouvé. Veuillez vous reconnecter.');
        setLoading(false);
        return;
      }

      const response = await axios.get(getApiUrl('/users'), {
        headers: {Authorization: `Bearer ${token}`}
      });
      setUsers(response.data);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des utilisateurs:', err);
      if (err.response?.status === 403) {
        setError('Accès refusé. Vous n\'avez pas les permissions nécessaires.');
      } else if (err.response?.status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
      } else {
        setError('Erreur lors du chargement des utilisateurs');
      }
    } finally {
      setLoading(false);
    }
  };

  // Logique de tri
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({key, direction});
  };

  // Calcul du tableau filtré et trié
  const processedUsers = useMemo(() => {
    let result = [...users];

    // 1. Filtrage par recherche
    if (searchTerm) {
      const lowSearch = searchTerm.toLowerCase();
      result = result.filter(user =>
        user.nom?.toLowerCase().includes(lowSearch) ||
        user.prenom?.toLowerCase().includes(lowSearch) ||
        user.mail?.toLowerCase().includes(lowSearch) ||
        user._id?.toLowerCase().includes(lowSearch)
      );
    }

    // 2. Filtrage par rôle
    if (roleFilter !== 'all') {
      result = result.filter(user => user.typeUser === roleFilter);
    }

    // 3. Tri
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Cas spéciaux (statut booléen)
        if (sortConfig.key === 'isActive') {
          aVal = a.isActive ? 'Actif' : 'Inactif';
          bVal = b.isActive ? 'Actif' : 'Inactif';
        }

        if (aVal < bVal) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [users, searchTerm, roleFilter, sortConfig]);

  const getStatusColor = (isActive) => {
    return isActive ? '#48bb78' : '#f56565';
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return '#1e3a5f';
      case 'moderateur': return '#667eea';
      case 'commercial': return '#48bb78';
      case 'installateur': return '#ed8936';
      case 'client': return '#38b2ac';
      case 'entreprise': return '#9f7aea';
      default: return '#718096';
    }
  };

  const BACKOFFICE_MODULES = [
    {id: 'dashboard', label: 'Dashboard'},
    {id: 'utilisateurs', label: 'Utilisateurs'},
    {id: 'clients', label: 'Clients'},
    {id: 'dossiers', label: 'Dossiers'},
    {id: 'produits', label: 'Produits'},
    {id: 'stock', label: 'Stock'},
    {id: 'commerciaux', label: 'Commerciaux'},
    {id: 'installateurs', label: 'Installateurs'},
    {id: 'equipes_installation', label: 'Équipes Installation'},
    {id: 'contrats', label: 'Contrats'},
    {id: 'documents', label: 'Documents'},
    {id: 'devis', label: 'Devis'},
    {id: 'prospects', label: 'Prospects'},
    {id: 'reclamations', label: 'Réclamations'},
    {id: 'partenaires', label: 'Partenaires'},
    {id: 'references', label: 'Références'},
    {id: 'fournisseurs', label: 'Fournisseurs'}
  ];

  const handlePermissionChange = (module, action, checked) => {
    const permString = `${module}:${action}`;
    setFormData(prev => {
      const newPerms = checked
        ? [...prev.permissions, permString]
        : prev.permissions.filter(p => p !== permString);
      return {...prev, permissions: newPerms};
    });
  };

  const handleToggleModulePermissions = (moduleId, checked) => {
    const moduleActions = moduleId === 'dashboard' ? ['read'] : ['read', 'create', 'update', 'delete'];
    const modulePerms = moduleActions.map(action => `${moduleId}:${action}`);

    setFormData(prev => {
      let newPerms = [...prev.permissions];
      if (checked) {
        // Ajouter toutes les permissions du module (éviter les doublons)
        modulePerms.forEach(p => {
          if (!newPerms.includes(p)) newPerms.push(p);
        });
      } else {
        // Supprimer toutes les permissions du module
        newPerms = newPerms.filter(p => !modulePerms.includes(p));
      }
      return {...prev, permissions: newPerms};
    });
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      nom: user.nom,
      prenom: user.prenom,
      mail: user.mail,
      typeUser: user.typeUser,
      isActive: user.isActive !== undefined ? user.isActive : true,
      permissions: user.permissions || []
    });
    setShowEditModal(true);
  };

  const renderPermissionsSelector = () => {
    if (formData.typeUser !== 'moderateur') return null;

    return (
      <div className="permissions-selector">
        <label className="section-title-perm">Permissions du Modérateur</label>
        <div className="permissions-grid">
          {BACKOFFICE_MODULES.map(module => {
            const moduleActions = module.id === 'dashboard' ? ['read'] : ['read', 'create', 'update', 'delete'];
            const modulePerms = moduleActions.map(action => `${module.id}:${action}`);
            const isModuleFullySelected = modulePerms.every(p => formData.permissions.includes(p));

            return (
              <div key={module.id} className={`module-permissions-card ${isModuleFullySelected ? 'is-full' : ''}`}>
                <div className="module-header-perm">
                  <span className="module-label">{module.label}</span>
                  <label className="toggle-all-module">
                    <input
                      type="checkbox"
                      checked={isModuleFullySelected}
                      onChange={(e) => handleToggleModulePermissions(module.id, e.target.checked)}
                    />
                    <span className="toggle-text">Toute la gestion</span>
                  </label>
                </div>
                <div className="actions-row">
                  {moduleActions.map(action => {
                    const permStr = `${module.id}:${action}`;
                    return (
                      <label key={permStr} className={`perm-checkbox ${formData.permissions.includes(permStr) ? action : ''}`}>
                        <input
                          type="checkbox"
                          checked={formData.permissions.includes(permStr)}
                          onChange={(e) => handlePermissionChange(module.id, action, e.target.checked)}
                        />
                        <span>{action}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDetail = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(getApiUrl(`/users/${user._id}`), {
        headers: {Authorization: `Bearer ${token}`}
      });
      setSelectedUser(response.data);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Erreur lors du chargement des détails:', err);
      setError('Erreur lors du chargement des détails de l\'utilisateur');
    }
  };

  const handleAdd = () => {
    setFormData({
      nom: '',
      prenom: '',
      mail: '',
      typeUser: 'user',
      isActive: true,
      permissions: []
    });
    setShowAddModal(true);
  };

  const handleConvertToClient = (user) => {
    setSelectedUser(user);
    setClientFormData({
      typeClient: 'BT',
      typeIdentifiant: 'cin',
      cin: '',
      passeport: '',
      telephone: user.telephone || user.numeroTelephone || '',
      email: user.mail || '',
      adresse: user.adresse || '',
      referenceSteg: '',
      puissance: '',
      district: '',
      typePaiement: 'comptant',
      etatDossier: 'Déposé chez Sastra',
      dateArrivDossier: new Date().toISOString().split('T')[0],
    });
    setShowConvertToClientModal(true);
  };

  const handleInputChange = (e) => {
    const {name, value} = e.target;
    const finalValue = name === 'isActive' ? value === 'true' : value;
    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
  };

  const handleClientInputChange = (e) => {
    const {name, value} = e.target;
    setClientFieldErrors(prev => ({
      ...prev,
      [name]: null,
      ...(name === 'typeIdentifiant' ? { cin: null, passeport: null } : {})
    }));
    if (name === 'typeIdentifiant') {
      setClientFormData(prev => ({
        ...prev,
        typeIdentifiant: value,
        cin: '',
        passeport: ''
      }));
    } else if (name === 'typeClient' && value === 'MT') {
      setClientFormData(prev => ({
        ...prev,
        typeClient: value,
        cin: '',
        passeport: ''
      }));
    } else {
      setClientFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    try {
      const token = localStorage.getItem('token');
      await axios.post(getApiUrl('/users'), formData, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setShowAddModal(false);
      fetchUsers();
      setMessage('Utilisateur ajouté avec succès');
    } catch (err) {
      console.error('Erreur lors de l\'ajout:', err);
      if (err.response?.status === 400) {
        setError('Données invalides. Vérifiez les informations saisies.');
      } else if (err.response?.status === 409) {
        setError('Cet email est déjà utilisé.');
      } else {
        setError('Erreur lors de l\'ajout de l\'utilisateur');
      }
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    try {
      const token = localStorage.getItem('token');
      await axios.put(getApiUrl(`/users/${selectedUser._id}`), formData, {
        headers: {Authorization: `Bearer ${token}`}
      });
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
      setMessage('Utilisateur modifié avec succès');
    } catch (err) {
      console.error('Erreur lors de la modification:', err);
      if (err.response?.status === 400) {
        setError('Données invalides. Vérifiez les informations saisies.');
      } else if (err.response?.status === 404) {
        setError('Utilisateur non trouvé.');
      } else {
        setError('Erreur lors de la modification de l\'utilisateur');
      }
    }
  };

  const handleDeleteConfirm = async () => {
    clearMessages();
    try {
      const token = localStorage.getItem('token');
      await axios.delete(getApiUrl(`/users/${selectedUser._id}`), {
        headers: {Authorization: `Bearer ${token}`}
      });
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
      setMessage('Utilisateur supprimé avec succès');
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      if (err.response?.status === 404) {
        setError('Utilisateur non trouvé.');
      } else {
        setError('Erreur lors de la suppression de l\'utilisateur');
      }
    }
  };

  const closeModals = () => {
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowAddModal(false);
    setShowDetailModal(false);
    setShowConvertToClientModal(false);
    setSelectedUser(null);
    setFormData({
      nom: '',
      prenom: '',
      mail: '',
      typeUser: 'user',
      isActive: true,
      permissions: []
    });
    setClientFormData({
      typeClient: 'BT',
      typeIdentifiant: 'cin',
      cin: '',
      passeport: '',
      telephone: '',
      email: '',
      adresse: '',
      referenceSteg: '',
      puissance: '',
      district: '',
      typePaiement: 'comptant',
      etatDossier: 'Déposé chez Sastra',
      dateArrivDossier: new Date().toISOString().split('T')[0],
    });
  };

  const handleConvertToClientSubmit = async (e) => {
    e.preventDefault();
    clearMessages();

    const fullForm = {
      ...clientFormData,
      nom: selectedUser?.nom || '',
      prenom: selectedUser?.prenom || ''
    };

    const clientVal = validateClientFormUtil(fullForm, { isEdit: false });
    const dossierVal = validateDossierFormUtil(fullForm, { typeClient: clientFormData.typeClient });
    const combinedErrors = { ...clientVal.errors, ...dossierVal.errors };

    if (!clientVal.isValid || !dossierVal.isValid) {
      setClientFieldErrors(combinedErrors);
      setError([...Object.values(clientVal.errors), ...Object.values(dossierVal.errors)].join('\n'));
      return;
    }

    try {
      const isMt = clientFormData.typeClient === 'MT';
      const token = localStorage.getItem('token');
      const dataToSend = {
        nom: selectedUser.nom,
        prenom: selectedUser.prenom,
        typeClient: clientFormData.typeClient,
        cin: !isMt && clientFormData.typeIdentifiant === 'cin' ? clientFormData.cin.trim() : undefined,
        passeport: !isMt && clientFormData.typeIdentifiant === 'passeport' ? clientFormData.passeport.trim() : undefined,
        telephone: clientFormData.telephone,
        email: clientFormData.email,
        adresse: clientFormData.adresse,
        // Premier dossier
        referenceSteg: clientFormData.referenceSteg,
        puissance: clientFormData.puissance ? parseFloat(clientFormData.puissance) : 0,
        district: clientFormData.district || null,
        typePaiement: clientFormData.typePaiement,
        etatDossier: clientFormData.etatDossier,
        dateArrivDossier: clientFormData.dateArrivDossier || new Date().toISOString().split('T')[0],
      };

      await axios.post(getApiUrl(`/users/${selectedUser._id}/convert-to-client`), dataToSend, {
        headers: {Authorization: `Bearer ${token}`}
      });

      setShowConvertToClientModal(false);
      setClientFieldErrors({});
      setSelectedUser(null);
      fetchUsers();
      setMessage('Utilisateur converti en client avec succès');
    } catch (err) {
      console.error('Erreur lors de la conversion:', err);
      setError(extractBackendError(err));
      setClientFieldErrors(mapBackendErrorToField(err));
    }
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'ascending' ? '↑' : '↓';
  };

  return (
    <div className="users-management-container">
      <BackOfficeMenu
        onNavigate={onNavigate}
        onLogout={onLogout}
        currentPage={currentPage}
      />
      <div className="users-management-content">
        <div className="dashboard-header">
          <div>
            <h1>Gestion des Utilisateurs</h1>
            <p>Administration des comptes utilisateurs</p>
          </div>
          {hasPermission('utilisateurs', 'create') && (
            <button className="btn-add-user" onClick={handleAdd}>Ajouter un utilisateur</button>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}

        <div className="table-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Rechercher (Nom, Email, ID...)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-box">
            <label>Filtrer par rôle :</label>
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="all">Tous les rôles</option>
              <option value="admin">Administrateur</option>
              <option value="moderateur">Modérateur</option>
              <option value="commercial">Commercial</option>
              <option value="installateur">Installateur</option>
              <option value="client">Client</option>
              <option value="entreprise">Entreprise</option>
              <option value="user">Utilisateur simple</option>
            </select>
          </div>
          <div className="table-stats">
            <span>Total : {processedUsers.length}</span>
          </div>
        </div>

        <div className="users-table-container">
          {loading ? (
            <LoadingSpinner message="Chargement des utilisateurs..." />
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('nom')} className="sortable">Nom {getSortIcon('nom')}</th>
                  <th onClick={() => requestSort('prenom')} className="sortable">Prénom {getSortIcon('prenom')}</th>
                  <th onClick={() => requestSort('mail')} className="sortable">Email {getSortIcon('mail')}</th>
                  <th onClick={() => requestSort('typeUser')} className="sortable">Rôle {getSortIcon('typeUser')}</th>
                  <th onClick={() => requestSort('isActive')} className="sortable">Statut {getSortIcon('isActive')}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {processedUsers.map((user) => (
                  <tr key={user._id}>
                    <td>{user.nom}</td>
                    <td>{user.prenom}</td>
                    <td>{user.mail}</td>
                    <td>
                      <span className="role-badge" style={{backgroundColor: getRoleColor(user.typeUser)}}>
                        {user.typeUser}
                      </span>
                    </td>
                    <td>
                      <span className="status-badge" style={{backgroundColor: getStatusColor(user.isActive)}}>
                        {user.isActive ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-detail" onClick={() => handleDetail(user)}>Détails</button>
                        {hasPermission('utilisateurs', 'update') && (
                          <button className="btn-edit" onClick={() => handleEdit(user)}>Modifier</button>
                        )}
                        {user.typeUser === 'user' && hasPermission('clients', 'create') && (
                          <button className="btn-convert" onClick={() => handleConvertToClient(user)}>
                            Convertir en Client
                          </button>
                        )}
                        {hasPermission('utilisateurs', 'delete') && (
                          <button className="btn-delete" onClick={() => handleDelete(user)}>Supprimer</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {processedUsers.length === 0 && (
                  <tr>
                    <td colSpan="6" style={{textAlign: 'center', padding: '40px', color: '#718096'}}>
                      Aucun utilisateur trouvé correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Modales - inchangées */}
        {showAddModal && (
          <div className="modal-overlay">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Ajouter un utilisateur</h2>
                <button className="modal-close" onClick={closeModals}>×</button>
              </div>
              <form onSubmit={handleAddSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Prénom</label>
                    <input type="text" name="prenom" value={formData.prenom} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Nom</label>
                    <input type="text" name="nom" value={formData.nom} onChange={handleInputChange} required />
                  </div>
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="mail" value={formData.mail} onChange={handleInputChange} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Rôle</label>
                    <select name="typeUser" value={formData.typeUser} onChange={handleInputChange}>
                      <option value="user">Utilisateur</option>
                      <option value="moderateur">Modérateur</option>
                      <option value="admin">Administrateur</option>
                      <option value="commercial">Commercial</option>
                      <option value="installateur">Installateur</option>
                      <option value="client">Client</option>
                      <option value="entreprise">Entreprise</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Statut</label>
                    <select name="isActive" value={formData.isActive.toString()} onChange={handleInputChange}>
                      <option value="true">Actif</option>
                      <option value="false">Inactif</option>
                    </select>
                  </div>
                </div>
                {renderPermissionsSelector()}
                <div className="modal-actions">
                  <button type="button" className="btn-cancel" onClick={closeModals}>Annuler</button>
                  <button type="submit" className="btn-save">Ajouter</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showEditModal && selectedUser && (
          <div className="modal-overlay">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Modifier l'utilisateur</h2>
                <button className="modal-close" onClick={closeModals}>×</button>
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Prénom</label>
                    <input type="text" name="prenom" value={formData.prenom} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>Nom</label>
                    <input type="text" name="nom" value={formData.nom} onChange={handleInputChange} required />
                  </div>
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" name="mail" value={formData.mail} onChange={handleInputChange} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Rôle</label>
                    <select name="typeUser" value={formData.typeUser} onChange={handleInputChange}>
                      <option value="user">Utilisateur</option>
                      <option value="moderateur">Modérateur</option>
                      <option value="admin">Administrateur</option>
                      <option value="commercial">Commercial</option>
                      <option value="installateur">Installateur</option>
                      <option value="client">Client</option>
                      <option value="entreprise">Entreprise</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Statut</label>
                    <select name="isActive" value={formData.isActive.toString()} onChange={handleInputChange}>
                      <option value="true">Actif</option>
                      <option value="false">Inactif</option>
                    </select>
                  </div>
                </div>
                {renderPermissionsSelector()}
                <div className="modal-actions">
                  <button type="button" className="btn-cancel" onClick={closeModals}>Annuler</button>
                  <button type="submit" className="btn-save">Enregistrer</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showDeleteModal && selectedUser && (
          <div className="modal-overlay">
            <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Confirmer la suppression</h2>
                <button className="modal-close" onClick={closeModals}>×</button>
              </div>
              <div className="modal-body">
                <p>Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{selectedUser.prenom} {selectedUser.nom}</strong> ?</p>
                <p className="warning-text">Cette action est irréversible.</p>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={closeModals}>Annuler</button>
                <button type="button" className="btn-delete-confirm" onClick={handleDeleteConfirm}>Supprimer</button>
              </div>
            </div>
          </div>
        )}

        {showDetailModal && selectedUser && <DetailUser user={selectedUser} onClose={closeModals} />}

        {showConvertToClientModal && selectedUser && (
          <div className="modal-overlay">
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>👤 Convertir en Client : {selectedUser.prenom} {selectedUser.nom}</h2>
                <button className="modal-close" onClick={closeModals}>×</button>
              </div>
              <form onSubmit={handleConvertToClientSubmit}>

                <h3 style={{marginTop: 0, borderBottom: '2px solid #e5e7eb', paddingBottom: 8}}>Informations Client</h3>

                {clientFormData.typeClient !== 'MT' && (
                  <>
                    {/* Type Identifiant + Champ dynamiquement affiché */}
                    <div className="form-group" style={{marginBottom: 12}}>
                      <label>Type d'identifiant *</label>
                      <div style={{display: 'flex', gap: 20, marginTop: 4}}>
                        <label style={{display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer'}}>
                          <input
                            type="radio"
                            name="typeIdentifiant"
                            value="cin"
                            checked={clientFormData.typeIdentifiant === 'cin'}
                            onChange={handleClientInputChange}
                          />
                          <span>CIN</span>
                        </label>
                        <label style={{display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer'}}>
                          <input
                            type="radio"
                            name="typeIdentifiant"
                            value="passeport"
                            checked={clientFormData.typeIdentifiant === 'passeport'}
                            onChange={handleClientInputChange}
                          />
                          <span>Passeport</span>
                        </label>
                      </div>
                    </div>
                  </>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>Type Client *</label>
                    <select name="typeClient" value={clientFormData.typeClient} onChange={handleClientInputChange} required>
                      <option value="BT">BT — Basse Tension (Particulier)</option>
                      <option value="MT">MT — Moyenne Tension (Professionnel)</option>
                    </select>
                  </div>
                  {clientFormData.typeClient !== 'MT' && (
                    clientFormData.typeIdentifiant === 'cin' ? (
                      <div className="form-group">
                        <label>CIN *</label>
                        <input
                          type="text"
                          name="cin"
                          value={clientFormData.cin}
                          onChange={handleClientInputChange}
                          required
                          placeholder="8 chiffres"
                          maxLength={8}
                          style={clientFieldErrors.cin ? { border: '1px solid #ef4444' } : {}}
                        />
                        {clientFieldErrors.cin && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{clientFieldErrors.cin}</span>}
                      </div>
                    ) : (
                      <div className="form-group">
                        <label>Numéro de passeport *</label>
                        <input
                          type="text"
                          name="passeport"
                          value={clientFormData.passeport}
                          onChange={handleClientInputChange}
                          required
                          placeholder="Numéro de passeport"
                          style={clientFieldErrors.passeport ? { border: '1px solid #ef4444' } : {}}
                        />
                        {clientFieldErrors.passeport && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{clientFieldErrors.passeport}</span>}
                      </div>
                    )
                  )}
                </div>

                {/* Téléphone + Email */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Téléphone</label>
                    <input type="tel" name="telephone" value={clientFormData.telephone} onChange={handleClientInputChange} placeholder="8 chiffres minimum" style={clientFieldErrors.telephone ? { border: '1px solid #ef4444' } : {}} />
                    {clientFieldErrors.telephone && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{clientFieldErrors.telephone}</span>}
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" name="email" value={clientFormData.email} onChange={handleClientInputChange} placeholder="email@exemple.com" style={clientFieldErrors.email ? { border: '1px solid #ef4444' } : {}} />
                    {clientFieldErrors.email && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{clientFieldErrors.email}</span>}
                  </div>
                </div>

                {/* Adresse */}
                <div className="form-group">
                  <label>Adresse</label>
                  <input type="text" name="adresse" value={clientFormData.adresse} onChange={handleClientInputChange} placeholder="Adresse complète" />
                </div>

                <h3 style={{marginTop: 20, borderBottom: '2px solid #e5e7eb', paddingBottom: 8}}>Premier Dossier</h3>

                {/* Référence STEG + Puissance */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Référence STEG *</label>
                    <input type="text" name="referenceSteg" value={clientFormData.referenceSteg} onChange={handleClientInputChange} required placeholder="Ex: 123456789" style={clientFieldErrors.referenceSteg ? { border: '1px solid #ef4444' } : {}} />
                    {clientFieldErrors.referenceSteg && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{clientFieldErrors.referenceSteg}</span>}
                  </div>
                  <div className="form-group">
                    <label>Puissance (kWc)</label>
                    <input type="number" name="puissance" value={clientFormData.puissance} onChange={handleClientInputChange} min="0" step="0.01" placeholder="Ex: 5.5" style={clientFieldErrors.puissance ? { border: '1px solid #ef4444' } : {}} />
                    {clientFieldErrors.puissance && <span className="field-error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{clientFieldErrors.puissance}</span>}
                  </div>
                </div>

                {/* District STEG */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Bureau STEG (District)</label>
                    <select name="district" value={clientFormData.district} onChange={handleClientInputChange}>
                      <option value="">— Sélectionner un bureau STEG —</option>
                      {stegList.map(s => (
                        <option key={s._id} value={s._id}>
                          {s.region}{s.numTel ? ` · ${s.numTel}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Type Paiement</label>
                    <select name="typePaiement" value={clientFormData.typePaiement} onChange={handleClientInputChange}>
                      <option value="comptant">Comptant</option>
                      <option value="credit">Crédit</option>
                    </select>
                  </div>
                </div>

                {/* État + Date arrivée dossier */}
                <div className="form-row">
                  <div className="form-group">
                    <label>État Dossier</label>
                    <select name="etatDossier" value={clientFormData.etatDossier} onChange={handleClientInputChange}>
                      <option value="Déposé chez Sastra">Déposé chez Sastra</option>
                      <option value="En attente d'approbation">En attente d'approbation</option>
                      <option value="En attente d'installation">En attente d'installation</option>
                      <option value="En attente de réception">En attente de réception</option>
                      <option value="Réceptionné">Réceptionné</option>
                      <option value="Manque PV">Manque PV</option>
                      <option value="Dossier en facturation">Dossier en facturation</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Date Arrivée Dossier</label>
                    <input type="date" name="dateArrivDossier" value={clientFormData.dateArrivDossier} onChange={handleClientInputChange} />
                  </div>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-cancel" onClick={closeModals}>Annuler</button>
                  <button type="submit" className="btn-save">✅ Convertir en Client</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersManagement;